/**
 * Pure maths behind the touch controls (no DOM): virtual joystick vector with
 * dead zone, the WASD / sprint mapping, and the tap / hold / drag classifier
 * for a finger on the world view. Smoke-tested in smoke/touch.ts.
 */

/** Fraction of the stick radius that reads as "centred". */
export const STICK_DEAD_ZONE = 0.18;
/** Past this deflection a direction key is considered pressed. */
export const STICK_KEY_THRESHOLD = 0.38;
/** Pushing the stick this far forward sprints (Bedrock: push fully forward). */
export const STICK_SPRINT = 0.93;
/** Finger travel (css px) that turns a touch into a look-drag. */
export const TAP_SLOP = 10;
/** A held finger starts mining / using after this long without moving (ms). */
export const HOLD_MS = 280;
/** Once mining, travel beyond this cancels it and turns into look (px). */
export const HOLD_CANCEL = 26;
/** Radians of turn per css px dragged, before the player's sensitivity option. */
export const TOUCH_LOOK = 0.0042;

export interface StickVec { x: number; y: number; mag: number }

/**
 * Stick offset (screen px, y down) -> unit-disc vector with y positive = forward.
 * The dead zone is rescaled out so the response starts at 0, not at the edge.
 */
export function stickVector(dx: number, dy: number, radius: number, dead = STICK_DEAD_ZONE): StickVec {
  const d = Math.hypot(dx, dy);
  if (radius <= 0 || d === 0) return { x: 0, y: 0, mag: 0 };
  const raw = Math.min(1, d / radius);
  if (raw <= dead) return { x: 0, y: 0, mag: 0 };
  const mag = (raw - dead) / (1 - dead);
  return { x: (dx / d) * mag, y: (-dy / d) * mag, mag };
}

export interface StickKeys { w: boolean; a: boolean; s: boolean; d: boolean }

export function stickKeys(v: StickVec, threshold = STICK_KEY_THRESHOLD): StickKeys {
  return { w: v.y > threshold, s: v.y < -threshold, a: v.x < -threshold, d: v.x > threshold };
}

/** Fully forward (within ~35 degrees of straight up) and fully deflected. */
export function stickSprints(v: StickVec): boolean {
  return v.mag >= STICK_SPRINT && v.y >= v.mag * 0.8;
}

export type GestureState = 'pending' | 'tap' | 'hold' | 'drag' | 'release';

/**
 * Where a finger on the world view is in its life.
 *  pending  -> still deciding
 *  tap      -> released quickly without moving: place / use / attack
 *  hold     -> still down past HOLD_MS (start mining / using); stays 'hold' while it lasts
 *  release  -> a hold ended (stop mining / using)
 *  drag     -> moved: it is a look gesture (also cancels an active hold)
 */
export function classifyGesture(elapsedMs: number, moved: number, released: boolean, holding: boolean): GestureState {
  if (holding) {
    if (moved > HOLD_CANCEL) return 'drag';
    return released ? 'release' : 'hold';
  }
  if (moved > TAP_SLOP) return 'drag';
  if (released) return elapsedMs < HOLD_MS ? 'tap' : 'release';
  return elapsedMs >= HOLD_MS ? 'hold' : 'pending';
}

/** Finger delta (px) -> yaw / pitch change (radians). Dragging right turns right. */
export function lookDelta(dx: number, dy: number, sensitivity = 1): { yaw: number; pitch: number } {
  return { yaw: -dx * TOUCH_LOOK * sensitivity, pitch: -dy * TOUCH_LOOK * sensitivity };
}
